/**
 * The small shared pieces: the panda mark, avatars, loading and error states,
 * form fields and the colour helper that turns a stored template colour name
 * into tokens.
 *
 * Keeping the error and empty states here rather than inline per page is what
 * makes "never a white screen" enforceable: a page that forgets to handle a
 * failure has nothing to render, a page that uses `ErrorState` cannot.
 */
import { AlertTriangle, Inbox, RefreshCw, WifiOff } from 'lucide-react';
import type { ReactNode } from 'react';
import { ApiError } from '../lib/api.ts';
import { useTween } from '../lib/animate.ts';
import { fmtDuration, fmtMoney, fmtMoneyShort, initialsOf, phoneDisplay, phoneHref } from '../lib/time.ts';

// ---------------------------------------------------------------------------
// Brand
// ---------------------------------------------------------------------------
export function PandaMark({ size = 32 }: { size?: number }) {
  const style = { width: size, height: size, objectFit: 'contain' as const };
  // Two files rather than a CSS filter: the green must survive untouched, and
  // a filter that lightens the ink would drag the brand colour with it.
  return (
    <>
      <img className="brand-art-light" src="/brand/sp-mark-192.png" width={size} height={size} alt="Sneaky Panda" style={style} />
      <img className="brand-art-dark" src="/brand/sp-mark-192-dark.png" width={size} height={size} alt="" aria-hidden="true" style={style} />
    </>
  );
}

/**
 * The full lockup: panda plus the sneaky panda wordmark, as supplied. Used
 * where there is room for the brand rather than just the mark.
 */
export function Lockup({ width = 168 }: { width?: number }) {
  const style = { width, height: 'auto' as const };
  return (
    <>
      <img className="brand-art-light" src="/brand/sp-lockup-560.png" alt="Sneaky Panda Staff App" style={style} />
      <img className="brand-art-dark" src="/brand/sp-lockup-560-dark.png" alt="" aria-hidden="true" style={style} />
    </>
  );
}

export function Wordmark({ size = 30 }: { size?: number }) {
  // Sized from the mark height so the two chrome positions stay in proportion.
  return <Lockup width={size * 5.2} />;
}

// ---------------------------------------------------------------------------
// Avatar. Initials on a colour derived from the name, so the same person is the
// same colour on every screen without storing anything.
// ---------------------------------------------------------------------------
const AVATAR_TOKENS = [
  'var(--sp-tpl-green)',
  'var(--sp-tpl-blue)',
  'var(--sp-tpl-red)',
  'var(--sp-tpl-purple)',
  'var(--sp-tpl-amber)',
  'var(--sp-person-teal)',
  'var(--sp-person-rose)',
  'var(--sp-person-moss)',
  'var(--sp-person-indigo)',
  'var(--sp-person-clay)',
  'var(--sp-person-lilac)',
  'var(--sp-tpl-slate)',
] as const;

function hashOf(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

/** A person's colour from their name alone, for places that see one person at a time. */
export function personColour(name: string): string {
  return AVATAR_TOKENS[hashOf(name) % AVATAR_TOKENS.length] ?? 'var(--sp-tpl-slate)';
}

/**
 * Colours for a whole team at once, dealt round the palette in a stable order
 * so no two of the first twelve people share one. A hash cannot promise that
 * (four of the Coffee Emporium's ten hashed to the same coral), and on a lane
 * where two bars sit one above the other, distinct beats consistent.
 */
export function personColours(names: Array<{ userId: string; name: string }>): Map<string, string> {
  const sorted = [...names].sort((a, b) => a.name.localeCompare(b.name) || a.userId.localeCompare(b.userId));
  const out = new Map<string, string>();
  sorted.forEach((p, i) => out.set(p.userId, AVATAR_TOKENS[i % AVATAR_TOKENS.length] ?? 'var(--sp-tpl-slate)'));
  return out;
}

export function Avatar({ name, size = 32 }: { name: string; size?: number }) {
  const colour = personColour(name);
  return (
    <span
      aria-hidden="true"
      className="inline-flex shrink-0 select-none items-center justify-center font-bold"
      style={{
        width: size,
        height: size,
        borderRadius: 'var(--sp-radius-pill)',
        background: colour,
        color: 'var(--sp-on-solid)',
        fontSize: Math.round(size * 0.38),
        letterSpacing: '0.01em',
        boxShadow: 'var(--sp-shadow-1)',
      }}
    >
      {initialsOf(name)}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Template and area colours. The DB stores a name ('green', 'amber', …); this
// is the only place that name becomes a token pair.
// ---------------------------------------------------------------------------
export type ColourName = 'green' | 'amber' | 'red' | 'blue' | 'purple' | 'slate';
export const COLOUR_NAMES: ColourName[] = ['green', 'amber', 'red', 'blue', 'purple', 'slate'];

export function colourTokens(name: string | null | undefined): { solid: string; soft: string } {
  const n = (name ?? 'slate').toLowerCase();
  const known = COLOUR_NAMES.includes(n as ColourName) ? n : 'slate';
  return { solid: `var(--sp-tpl-${known})`, soft: `var(--sp-tpl-${known}-soft)` };
}

// ---------------------------------------------------------------------------
// States
// ---------------------------------------------------------------------------
export function Spinner({ size = 18, label = 'Loading' }: { size?: number; label?: string }) {
  return (
    <span
      role="status"
      aria-label={label}
      className="inline-block animate-spin"
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        border: '2px solid var(--sp-border)',
        borderTopColor: 'var(--sp-accent)',
      }}
    />
  );
}

/**
 * A skeleton in the rough shape of a list page: a heading, a card of rows.
 * The data replaces it in place, so nothing jumps when it arrives. The label
 * is there for screen readers and for anyone who waits long enough to wonder.
 */
export function LoadingBlock({ label = 'Loading', rows = 4 }: { label?: string; rows?: number }) {
  return (
    <div className="mx-auto max-w-5xl px-4 py-6" role="status" aria-label={label}>
      <span className="skel mb-5" style={{ width: 180, height: 22 }} />
      <div className="card overflow-hidden">
        {Array.from({ length: rows }, (_, i) => (
          <div
            key={i}
            className="flex items-center gap-3 px-4 py-3.5"
            style={{ borderTop: i > 0 ? '1px solid var(--sp-hairline)' : undefined }}
          >
            <span className="skel shrink-0" style={{ width: 34, height: 34, borderRadius: 'var(--sp-radius-pill)' }} />
            <span className="flex flex-1 flex-col gap-2">
              <span className="skel" style={{ width: `${46 + ((i * 17) % 30)}%`, height: 13 }} />
              <span className="skel" style={{ width: `${28 + ((i * 11) % 20)}%`, height: 11 }} />
            </span>
            <span className="skel" style={{ width: 64, height: 12 }} />
          </div>
        ))}
      </div>
      <p className="mt-4 flex items-center justify-center gap-2 text-[12.5px] text-faint">
        <Spinner size={13} label={label} />
        {label}
      </p>
    </div>
  );
}

/** The roster's own skeleton: a week bar, a palette, and a grid with a few
 *  cards scattered where shifts will be. */
export function RosterSkeleton() {
  const cols = 'var(--sp-staff-col) repeat(7, minmax(var(--sp-day-col), 1fr))';
  return (
    <div role="status" aria-label="Loading the roster" className="flex flex-col overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: '1px solid var(--sp-border)' }}>
        <span className="skel" style={{ width: 38, height: 38 }} />
        <span className="skel" style={{ width: 38, height: 38 }} />
        <span className="skel ml-2" style={{ width: 200, height: 18 }} />
        <span className="flex-1" />
        <span className="skel hidden md:block" style={{ width: 130, height: 38 }} />
        <span className="skel hidden md:block" style={{ width: 130, height: 38 }} />
      </div>
      <div
        className="flex items-center gap-2 px-4 py-2.5"
        style={{ background: 'var(--sp-surface-2)', borderBottom: '1px solid var(--sp-border)' }}
      >
        {[0, 1, 2].map((i) => (
          <span key={i} className="skel" style={{ width: 120, height: 36 }} />
        ))}
      </div>
      <div style={{ minWidth: 'var(--sp-roster-min-w)' }}>
        {Array.from({ length: 7 }, (_, r) => (
          <div
            key={r}
            className="grid"
            style={{ gridTemplateColumns: cols, borderBottom: '1px solid var(--sp-hairline)' }}
          >
            <div className="flex items-center gap-2.5 px-3 py-3">
              <span className="skel shrink-0" style={{ width: 32, height: 32, borderRadius: 'var(--sp-radius-pill)' }} />
              <span className="skel" style={{ width: '60%', height: 12 }} />
            </div>
            {Array.from({ length: 7 }, (_, c) => (
              <div
                key={c}
                className="p-1.5"
                style={{ borderLeft: '1px solid var(--sp-hairline)', minHeight: 'var(--sp-row-h)' }}
              >
                {(r * 3 + c) % 4 === 0 ? (
                  <span className="skel" style={{ height: 44, animationDelay: `${(r + c) * 60}ms` }} />
                ) : null}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Live figures. A number that changes is tweened rather than replaced, so a
// drop on the roster reads as "that cost $240" instead of "different screen".
// ---------------------------------------------------------------------------
export function TweenMoney({ value, short = false, cents }: { value: number; short?: boolean; cents?: boolean }) {
  const v = useTween(value);
  return <>{short ? fmtMoneyShort(v) : fmtMoney(v, { cents })}</>;
}

export function TweenDuration({ minutes }: { minutes: number }) {
  const v = useTween(minutes);
  return <>{fmtDuration(Math.round(v))}</>;
}

/**
 * The single failure surface. It distinguishes "cannot reach the server" from
 * "the server refused", because on a dev machine the first is nearly always the
 * API simply not being up, and saying so saves ten minutes of confusion.
 */
export function ErrorState({
  error,
  onRetry,
  what = 'this page',
}: {
  error: unknown;
  onRetry?: () => void;
  what?: string;
}) {
  const offline = error instanceof ApiError && error.isOffline;
  const message =
    error instanceof ApiError
      ? error.message
      : `Something went wrong loading ${what}. Try again in a moment.`;

  return (
    <div className="mx-auto max-w-md px-6 py-14 text-center">
      <span
        className="mx-auto mb-4 flex h-12 w-12 items-center justify-center"
        style={{
          borderRadius: 'var(--sp-radius-pill)',
          background: 'var(--sp-warn-soft)',
          color: 'var(--sp-warn-strong)',
        }}
      >
        {offline ? <WifiOff size={22} aria-hidden="true" /> : <AlertTriangle size={22} aria-hidden="true" />}
      </span>
      <h2 className="text-[16px] font-bold text-ink">
        {offline ? 'The server is not answering' : `Could not load ${what}`}
      </h2>
      <p className="mx-auto mt-2 max-w-sm text-[13.5px] leading-relaxed text-muted">{message}</p>
      {offline ? (
        <p className="mx-auto mt-2 max-w-sm text-[12.5px] leading-relaxed text-faint">
          If you are running this locally, the API needs to be up on port 3720.
        </p>
      ) : null}
      {onRetry ? (
        <button type="button" className="btn mt-5" onClick={onRetry}>
          <RefreshCw size={15} aria-hidden="true" />
          Try again
        </button>
      ) : null}
    </div>
  );
}

export function EmptyState({
  title,
  body,
  action,
  icon,
}: {
  title: string;
  body?: string;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="anim-rise mx-auto max-w-sm px-6 py-14 text-center">
      <span className="empty-well mx-auto mb-5">{icon ?? <Inbox size={22} aria-hidden="true" />}</span>
      <h3 className="text-[15px] font-bold text-ink">{title}</h3>
      {body ? <p className="mt-1.5 text-[13.5px] leading-relaxed text-muted">{body}</p> : null}
      {action ? <div className="mt-4 flex justify-center">{action}</div> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Form field. One wrapper so the label, the invalid marking and the error line
// are identical everywhere and cannot be half-implemented on one screen.
// ---------------------------------------------------------------------------
export function Field({
  label,
  error,
  hint,
  children,
  id,
}: {
  label: string;
  error?: string | null;
  hint?: string;
  children: ReactNode;
  id?: string;
}) {
  return (
    <div className="mb-4">
      <label className="field-label" htmlFor={id}>
        {label}
      </label>
      {children}
      {error ? (
        <span className="field-error" role="alert">
          {error}
        </span>
      ) : hint ? (
        <span className="mt-1 block text-[12px] leading-snug text-faint">{hint}</span>
      ) : null}
    </div>
  );
}

/** Every phone number: a tel: link, with the number itself made unbreakable. */
export function PhoneLink({ value, className }: { value: string; className?: string }) {
  const display = phoneDisplay(value);
  return (
    <a href={phoneHref(value)} className={className} style={{ color: 'inherit' }}>
      {display.split(' ').map((part, i) => (
        <span key={`${part}-${i}`}>
          {i > 0 ? ' ' : ''}
          {part}
        </span>
      ))}
    </a>
  );
}

/** A labelled figure. Used by the totals bar and every summary strip. */
export function Stat({
  label,
  value,
  sub,
  tone = 'default',
  tip,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: 'default' | 'accent' | 'warn';
  tip?: string;
}) {
  const colour =
    tone === 'accent' ? 'var(--sp-accent-strong)' : tone === 'warn' ? 'var(--sp-warn-strong)' : 'var(--sp-text)';
  return (
    <div data-tip={tip} className="min-w-0">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-faint">{label}</div>
      <div className="tabular truncate text-[19px] font-extrabold leading-tight" style={{ color: colour }}>
        {value}
      </div>
      {sub ? <div className="tabular truncate text-[12px] text-muted">{sub}</div> : null}
    </div>
  );
}
